import { wrap } from "@reatom/core"
import { decodeJwt } from "jose"
import { z } from "zod"

const channels = z.object({ inApp: z.boolean(), email: z.boolean() })
export const preferencesSchema = z.object({
  preferences: z.object({
    tradingActivity: channels,
    gachaCollectibles: channels,
    dropsSeries: channels,
    pointsRewards: channels,
  }),
  emailConnected: z.boolean(),
})
export const consentStatusSchema = z.object({
  pendingIntentId: z.string().nullable(),
  emailConnected: z.boolean(),
})
export const promoStatusSchema = z.object({
  variant: z.enum(["bind-email", "turn-on-alerts"]),
  actionId: z.string().nullable(),
  eligible: z.boolean(),
  dismissedUntil: z.string().nullable(),
  emailConnected: z.boolean(),
})
export type AccountSettingsOptions = {
  bffBaseUrl: string
  accountId: string
  getAccessToken: () => Promise<string | null>
}
type Payload =
  | { consent: true }
  | { intentId: string }
  | { consent: true; actionId: string }
  | { category: string; channel: string; enabled: boolean }

export async function accountSettingsRequest(
  options: AccountSettingsOptions,
  path: "email-consent" | "email-promo" | "notifications",
  method: "GET" | "POST" | "PUT" | "PATCH" = "GET",
  payload?: Payload,
) {
  const token = await wrap(options.getAccessToken())
  if (!token) throw new Error("Sign in again to manage your email.")
  if (decodeJwt(token).sub !== options.accountId)
    throw new Error("Your Account changed. Please try again.")
  const response = await wrap(
    fetch(`${options.bffBaseUrl.replace(/\/$/, "")}/api/v1/account/${path}`, {
      method,
      cache: "no-store",
      headers: {
        authorization: `Bearer ${token}`,
        "x-account-id": options.accountId,
        ...(payload ? { "content-type": "application/json" } : {}),
      },
      ...(payload ? { body: JSON.stringify(payload) } : {}),
    }),
  )
  const body = await wrap(response.json())
  if (!response.ok) {
    const error = z.object({ error: z.string() }).safeParse(body)
    throw new Error(
      error.success
        ? error.data.error
        : "Unable to save email settings. Please try again.",
    )
  }
  return body
}

export async function getNotificationPreferences(
  options: AccountSettingsOptions,
) {
  return preferencesSchema.parse(
    await wrap(accountSettingsRequest(options, "notifications")),
  )
}
